import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, ChevronRight } from "lucide-react";
// import { toast } from "sonner"; // re-enable with the "Capture on the go" button below
import { FilterChips, Pill, AskStatePill, EmptyState } from "@/design/components";
import { cn } from "@/design/cn";
import { formatRelativeShort } from "@/design/format";
import { useSeed, usePeopleIndex, useMoney, useActorId, useProjects } from "@/domain/hooks";
import { useOfflineQueue } from "@/domain/offlineQueue";
import { hasOpenQuery } from "@/domain/status";
import type { SpendRequest, SpendState } from "@/domain/types";
import { api, demoModeActive } from "@/domain/api";
import { normalizeSpendRequest } from "@/domain/normalizeSpend";
import { fetchWithOfflineCache } from "@/domain/offlineDb";
import { QueryReplyDrawer } from "./QueryReplyDrawer";

// A spend ask that's still sitting in the offline queue, not yet on the
// server - built straight from the queued payload (see useMutations.ts's
// useRaiseSpend), not persisted anywhere of its own. Once the real sync
// completes the queue row disappears and the next dbMine refetch (which
// TanStack Query already does automatically on reconnect) picks up the real
// server row in its place - so there's nothing to reconcile here.
export function pendingActionToDisplayAsk(action: { id: string; payload: unknown; createdAt: number }, reporterId: string): SpendRequest {
  const p = (action.payload ?? {}) as Record<string, unknown>;
  return {
    id: `queue-${action.id}`,
    reporterId,
    organisationId: "",
    projectId: String(p.projectId ?? ""),
    subProjectId: p.subProjectId != null ? String(p.subProjectId) : undefined,
    milestoneId: p.milestoneId != null ? String(p.milestoneId) : undefined,
    title: String(p.title ?? ""),
    amount: Number(p.amount ?? 0),
    currency: "UGX",
    category: p.category as SpendRequest["category"],
    supplier: p.supplier as string | undefined,
    purpose: String(p.description ?? ""),
    documents: [],
    state: "proposed",
    submittedAt: new Date(action.createdAt).toISOString(),
  };
}

// Evidence-eligible window: a reporter may open the evidence screen — and submit
// evidence again — for any post-approval, pre-confirmation ask. Locked once the
// ask is confirmed or rejected. See §6 spend flow.
const EVIDENCE_STATES: SpendState[] = ["approved_for_spend", "committed", "evidence_submitted"];

// Inbox filter chips. Labels mirror the reporter status vocabulary (ASK_STATE_REPORTER
// in domain/status.ts) so the filters read in the same words as the reporter's own
// status pills. `states: null` = show everything ("All"). committed + evidence_submitted
// share one status for the reporter ("Waiting to be confirmed"), so they share a chip.
// Every non-terminal state maps to exactly one chip, so no ask is reachable only via
// "All"; counts and rows both derive from this table so they can never drift apart.
// A filter matches by explicit state list, or by a predicate where the chip is a derived
// sub-state rather than a raw state. "Queried" and "Waiting for approval" both live inside
// `proposed`: a queried ask has an open reviewer query (see hasOpenQuery); a waiting ask
// does not. Splitting them by predicate keeps every proposed ask under exactly one chip.
type InboxFilter = { value: string; label: string; states: SpendState[] | null; match?: (r: SpendRequest) => boolean };
const INBOX_FILTERS: InboxFilter[] = [
  { value: "all", label: "All", states: null },
  { value: "waiting_approval", label: "Waiting for approval", states: ["proposed"], match: (r) => r.state === "proposed" && !hasOpenQuery(r) },
  { value: "ready_to_spend", label: "Ready to spend", states: ["approved_for_spend"] },
  { value: "waiting_confirm", label: "Waiting to be confirmed", states: ["committed", "evidence_submitted"] },
  { value: "queried", label: "Queried", states: null, match: hasOpenQuery },
  { value: "paid", label: "Paid", states: ["confirmed"] },
  { value: "not_approved", label: "Rejected", states: ["rejected"] },
];

// Does this ask belong under this filter chip? Predicate wins; else the state list; else all.
function matchesFilter(f: InboxFilter, r: SpendRequest): boolean {
  if (f.match) return f.match(r);
  if (f.states) return f.states.includes(r.state);
  return true;
}

export function ReporterInbox() {
  const seed = useSeed();
  const people = usePeopleIndex();
  const money = useMoney();
  const actorId = useActorId();
  const navigate = useNavigate();
  // Demo mode keeps the whole loop in the seed store (mutations write there, and the
  // query thread only lives there), so the inbox must read the seed too — otherwise a
  // queried ask never surfaces to the reporter. Live production reads the DB (my-requests
  // is scoped to the signed-in user server-side), aggregated across the workspace projects.
  const demo = demoModeActive();
  const [filter, setFilter] = useState("all");
  const [queryAsk, setQueryAsk] = useState<SpendRequest | null>(null);

  // A real useQuery (not a one-shot effect) so TanStack Query's default
  // refetch-on-reconnect kicks in - previously this only ever fetched once
  // on mount, so an ask that synced from the offline queue after a
  // reconnect never showed up until a manual page reload.
  //
  // useProjects() here, not seed.projects directly: seed is always the demo
  // seed blob (GET /api/workspace-seed - genuinely demo/fixture data, per
  // that route's own doc comment, hydrated regardless of mode), so its
  // project ids are fake slugs like "fs-04-entebbe", never real numeric DB
  // ids. useProjects() returns the real API-backed project list (real
  // numeric ids) when available and only falls back to the demo list
  // otherwise - reading seed.projects directly here meant every one of
  // these real-mode requests below was doomed to fail against the real
  // backend (which rejects a non-numeric :projectId), so this screen never
  // showed real data in real mode regardless of what actually existed.
  // Sorted (not just mapped) so a transient reorder of useProjects()'s
  // result (e.g. a background refetch settling in a different sequence)
  // can't make TanStack Query hash this into a *different*, uncached query
  // key mid-session - which would look like existing raises vanishing,
  // since a fresh key has no data until it fetches successfully again.
  const projectIds = useProjects().map((p) => p.id).sort();
  const projectIdsKey = projectIds.join(",");
  const { data: dbMineData, isLoading: dbLoading, isError: loadError } = useQuery({
    queryKey: ["reporter-inbox", projectIdsKey],
    queryFn: () =>
      fetchWithOfflineCache(`reporter-inbox:${projectIdsKey}`, async () => {
        const results = await Promise.allSettled(projectIds.map((id) => api.getMySpendRequests(id)));
        const fulfilled = results.filter((r): r is PromiseFulfilledResult<Record<string, unknown>[]> => r.status === "fulfilled");
        if (results.length > 0 && fulfilled.length === 0) {
          throw new Error("Unable to load requests from the database");
        }
        return fulfilled.flatMap((r) => r.value).map(normalizeSpendRequest);
      }),
    enabled: !demo,
    // Same class of gap fixed earlier this session for useOfflineQueue():
    // without this, TanStack Query's default networkMode "online" pauses
    // this query (and any refetch an invalidation triggers) while offline,
    // so the Inbox could only ever show its last-cached data frozen at
    // whatever it was BEFORE going offline - or nothing at all if this was
    // the query's first-ever mount. fetchWithOfflineCache above gives it
    // somewhere to actually fall back to.
    networkMode: "always",
  });
  const dbMine = dbMineData ?? [];
  const loading = !demo && dbLoading;

  // Asks raised while offline that haven't synced to the server yet - shown
  // immediately instead of vanishing into the queue with no trace in the UI.
  // Failed ones are included too (not just pending/syncing) - a raise that
  // couldn't sync must stay visible here, not just in ReporterShell's
  // separate "needs attention" banner, or it reads as having disappeared.
  const { pending, syncing, failed } = useOfflineQueue();
  const pendingAsks = useMemo(
    () =>
      [...pending, ...syncing]
        .filter((a) => a.type === "raiseSpend")
        .map((a) => pendingActionToDisplayAsk(a, actorId)),
    [pending, syncing, actorId],
  );
  const failedAsks = useMemo(
    () =>
      failed
        .filter((a) => a.type === "raiseSpend")
        .map((a) => pendingActionToDisplayAsk(a, actorId)),
    [failed, actorId],
  );
  // One id → queue status, so a row's pill can distinguish "queued, not yet
  // attempted" from "actively syncing" from "needs attention" instead of
  // collapsing the first two into one "Syncing…" state — a raise submitted
  // offline sits as "pending" until flushQueue actually picks it up.
  const queueStatusById = useMemo(() => {
    const m = new Map<string, "pending" | "syncing" | "failed">();
    for (const a of pending) if (a.type === "raiseSpend") m.set(`queue-${a.id}`, "pending");
    for (const a of syncing) if (a.type === "raiseSpend") m.set(`queue-${a.id}`, "syncing");
    for (const a of failed) if (a.type === "raiseSpend") m.set(`queue-${a.id}`, "failed");
    return m;
  }, [pending, syncing, failed]);

  const mine = demo ? seed.spendRequests.filter((r) => r.reporterId === actorId) : [...pendingAsks, ...failedAsks, ...dbMine];

  const countFor = (f: InboxFilter) => mine.filter((r) => matchesFilter(f, r)).length;
  const activeFilter = INBOX_FILTERS.find((f) => f.value === filter) ?? INBOX_FILTERS[0];
  const rows = mine.filter((r) => matchesFilter(activeFilter, r));

  // Approved asks the reporter still has to spend and evidence ("Ready to spend").
  // Same set as the "Ready to spend" filter, so the banner count and that filter agree.
  const readyToSpend = mine.filter((r) => r.state === "approved_for_spend");
  // Queried asks awaiting the reporter's reply (spec §6.3) — drive the reply banner.
  const needsReply = mine.filter(hasOpenQuery);

  return (
    <div className="relative">
      {needsReply.length > 0 && (
        <button
          onClick={() => {
            if (needsReply.length === 1) setQueryAsk(needsReply[0]);
            else setFilter("queried");
          }}
          className="focus-ring flex w-full items-center justify-between border-b border-warn/30 bg-warnSoft px-4 py-3.5 text-left"
        >
          <div>
            <span className="text-sm font-semibold text-warn">
              <span className="tabular-nums">{needsReply.length}</span> spend ask{needsReply.length > 1 ? "s" : ""} queried — your reply is needed
            </span>
            <p className="mt-0.5 text-xs text-warn/80">
              {needsReply.length === 1
                ? "Your contractor lead asked for more information. Tap to reply."
                : "Your contractor lead asked for more information. Tap to review them."}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-warn" />
        </button>
      )}

      {readyToSpend.length > 0 && (
        <button
          onClick={() => {
            // One waiting ask → open it directly. More than one → the banner can't
            // sensibly open several evidence screens, so show the "Ready to spend" list.
            if (readyToSpend.length === 1) navigate(`/reporter/evidence/${readyToSpend[0].id}`);
            else setFilter("ready_to_spend");
          }}
          className="focus-ring flex w-full items-center justify-between border-b border-warn/30 bg-warnSoft px-4 py-3.5 text-left"
        >
          <div>
            <span className="text-sm font-semibold text-warn">
              <span className="tabular-nums">{readyToSpend.length}</span> approved spend ask{readyToSpend.length > 1 ? "s" : ""} waiting for your evidence
            </span>
            <p className="mt-0.5 text-xs text-warn/80">
              {readyToSpend.length === 1
                ? "Tap to record your spend and attach evidence."
                : "Tap to review the asks that are ready to spend."}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-warn" />
        </button>
      )}

      <div className="p-4">
        <h1 className="text-xl font-semibold text-ink">Inbox</h1>
        <div className="mt-4">
          <FilterChips
            options={INBOX_FILTERS.map((f) => ({
              value: f.value,
              label: f.label,
              // "All" always shows its count; status chips hide a zero count.
              count: f.value === "all" ? countFor(f) : countFor(f) || undefined,
            }))}
            value={filter}
            onChange={setFilter}
          />
        </div>

        {/* "Capture on the go" (WhatsApp receipt capture) is commented out until the
            WhatsApp intake is functional. Re-enable this block — and the `toast` and
            `MessageCircle` imports above — when it works.
        <button
          onClick={() => toast.message("Send a photo of the receipt to +256 800 LEDGER on WhatsApp. It lands here as a draft spend.")}
          className="focus-ring mt-4 flex w-full items-center gap-3 rounded-control border border-dashed border-line px-4 py-3 text-left text-base text-mute hover:border-brand hover:text-ink"
        >
          <MessageCircle className="h-5 w-5 shrink-0 text-faint" />
          Capture on the go: send a receipt photo by WhatsApp to log spend hands-free.
        </button>
        */}

        {loading ? (
          <EmptyState className="mt-8" title="Loading requests" body="Fetching your spend requests from the database…" />
        ) : loadError ? (
          <EmptyState className="mt-8" title="Inbox unavailable" body="Unable to load your requests from the database." />
        ) : rows.length === 0 ? (
          <EmptyState
            className="mt-8"
            title="Nothing here"
            body={filter === "all" ? "Raise a spend ask to get started." : "No asks match this filter."}
          />
        ) : (
          <ul className="mt-4 space-y-3">
            {rows.map((r) => (
              <InboxCard
                key={r.id}
                ask={r}
                money={money}
                reviewerName={people[r.reviewerId ?? ""]?.fullName}
                syncStatus={queueStatusById.get(r.id)}
                onClick={() => {
                  if (queueStatusById.has(r.id)) return;
                  if (hasOpenQuery(r)) setQueryAsk(r);
                  else if (EVIDENCE_STATES.includes(r.state)) navigate(`/reporter/evidence/${r.id}`);
                }}
              />
            ))}
          </ul>
        )}
      </div>

      <QueryReplyDrawer ask={queryAsk} open={!!queryAsk} onOpenChange={(o) => !o && setQueryAsk(null)} />

      <button
        onClick={() => navigate("/reporter/raise")}
        aria-label="Raise spend ask"
        className="focus-ring fixed bottom-20 right-4 z-10 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-overlay md:hidden"
      >
        <Plus className="h-6 w-6" />
      </button>
    </div>
  );
}

function InboxCard({
  ask,
  reviewerName,
  money,
  syncStatus,
  onClick,
}: {
  ask: SpendRequest;
  reviewerName?: string;
  money: (n: number) => string;
  /** Set only for a row backed by the offline queue, not a real server record yet. */
  syncStatus?: "pending" | "syncing" | "failed";
  onClick: () => void;
}) {
  const meta = stateMeta(ask, reviewerName);
  // A queried ask is still `proposed`, but the raw state pill would read "Waiting for
  // approval" — which contradicts the query. Show a "Queried" pill (warn) instead.
  const queried = hasOpenQuery(ask);
  return (
    <li>
      <button
        onClick={onClick}
        className={cn(
          "focus-ring w-full rounded-card border border-line bg-card p-4 text-left",
          ask.state === "approved_for_spend" && "border-l-4 border-l-brand",
          queried && "border-l-4 border-l-warn",
          syncStatus === "failed" && "border-l-4 border-l-bad",
          syncStatus && "opacity-80",
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <span className="text-base font-medium text-ink">{ask.title}</span>
          <span className="shrink-0 text-base font-medium tabular-nums text-ink">{money(ask.amount)}</span>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {syncStatus === "pending" ? (
            <Pill variant="warn">Waiting to sync</Pill>
          ) : syncStatus === "syncing" ? (
            <Pill variant="warn">Syncing…</Pill>
          ) : syncStatus === "failed" ? (
            <Pill variant="danger">Couldn't sync</Pill>
          ) : queried ? (
            <Pill variant="warn">Queried</Pill>
          ) : (
            <AskStatePill state={ask.state} />
          )}
          {ask.category && <Pill variant="neutral">{ask.category}</Pill>}
          <span className="data text-xs text-faint">{formatRelativeShort(ask.submittedAt)}</span>
        </div>
        {syncStatus === "pending" ? (
          <p className="mt-2 text-sm text-mute">Saved offline — will send once you're back online.</p>
        ) : syncStatus === "syncing" ? (
          <p className="mt-2 text-sm text-mute">Sending now…</p>
        ) : syncStatus === "failed" ? (
          <p className="mt-2 text-sm text-bad">Couldn't reach the server — retry or discard it from the banner above.</p>
        ) : (
          meta.sub && <p className={`mt-2 whitespace-pre-line text-sm ${meta.subTone}`}>{meta.sub}</p>
        )}
      </button>
    </li>
  );
}

function stateMeta(ask: SpendRequest, reviewerName?: string): { sub?: string; subTone: string } {
  switch (ask.state) {
    case "proposed":
      return ask.reviewerComment
        ? { sub: ask.reviewerComment, subTone: "text-warnStrong" }
        : { sub: "Awaiting your contractor lead", subTone: "text-mute" };
    case "approved_for_spend":
      return ask.path === "recorded"
        ? { sub: "Within your authority. Tap to add evidence.", subTone: "text-mute" }
        : { sub: `Approved by ${reviewerName ?? "your lead"}. Tap to submit evidence.`, subTone: "text-mute" };
    case "committed":
      return { sub: "Spend items logged. Tap to add more evidence.", subTone: "text-mute" };
    case "evidence_submitted":
      return { sub: "Evidence submitted. Tap to add more evidence.", subTone: "text-mute" };
    case "confirmed":
      return { subTone: "text-mute" };
    case "rejected":
      return { sub: ask.rejectionReason, subTone: "text-badStrong" };
    default:
      return { subTone: "text-mute" };
  }
}
